import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { WeeklyHoursEditor } from "./WeeklyHoursEditor";
import { SaveBar } from "./Field";
import { SpecialDates } from "./SpecialDates";
import { ConflictDialog } from "./ConflictDialog";
import { DAYS, dayError, emptyWeek, fmtPeriods } from "@/lib/businessHours";

const valid = (w) => DAYS.every(([k]) => !dayError(w[k]));

function useSaveWithConflicts(check, save) {
  const [list, setList] = useState(null);
  const go = async () => {
    try { const c = (await check()).data.conflicts; if (c.length) return setList(c); } catch (e) { return toast.error(apiErr(e)); }
    save.mutate();
  };
  return { go, list, close: () => setList(null), confirm: () => { setList(null); save.mutate(); } };
}

function BusinessWeekly({ data, onDirty }) {
  const qc = useQueryClient();
  const [week, setWeek] = useState(data.weekly);
  useEffect(() => setWeek(data.weekly), [data.weekly]);
  const dirty = JSON.stringify(week) !== JSON.stringify(data.weekly);
  useEffect(() => { onDirty("hours", dirty); }, [dirty, onDirty]);
  const save = useMutation({
    mutationFn: () => api.put("/business/hours", { weekly: week }),
    onSuccess: (r) => { qc.setQueryData(["business-hours"], r.data); toast.success("Operating hours saved"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const flow = useSaveWithConflicts(() => api.post("/business/hours/conflicts", { weekly: week }), save);
  return (
    <>
      <WeeklyHoursEditor week={week} onChange={setWeek} disabled={!data.can_edit} prefix="hours" />
      {data.can_edit && <SaveBar id="hours" dirty={dirty} busy={save.isPending} disabled={!valid(week)} onSave={flow.go} onCancel={() => setWeek(data.weekly)} />}
      <ConflictDialog list={flow.list} onClose={flow.close} onConfirm={flow.confirm} busy={save.isPending} />
    </>
  );
}

function BranchHours({ b, defaultWeek, canEdit }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(b.mode);
  const [week, setWeek] = useState(b.weekly || defaultWeek);
  const save = useMutation({
    mutationFn: () => api.put(`/business/branches/${b.id}/hours`, { mode, weekly: mode === "custom" ? week : undefined }),
    onSuccess: (r) => { qc.setQueryData(["business-hours"], r.data); setOpen(false); toast.success(`${b.name} hours saved`); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const flow = useSaveWithConflicts(() => api.post("/business/hours/conflicts", { branch_id: b.id, mode, weekly: mode === "custom" ? week : defaultWeek }), save);
  return (
    <div className="rounded-lg border border-slate-200 px-3 py-2.5" data-testid={`branch-hours-row-${b.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><div className="font-medium text-slate-800">{b.name}{b.is_primary && <span className="ml-1 text-xs text-emerald-600">· primary</span>}</div>
          <div className="text-xs text-slate-500" data-testid={`branch-hours-mode-${b.id}`}>{b.mode === "custom" ? "Custom hours" : "Uses business hours"}</div></div>
        {canEdit && <Button size="sm" variant="outline" onClick={() => { setMode(b.mode); setWeek(b.weekly || defaultWeek); setOpen(true); }} data-testid={`branch-hours-edit-${b.id}`}>Edit</Button>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`branch-hours-dialog-${b.id}`}>
          <DialogHeader><DialogTitle>{b.name} — operating hours</DialogTitle><DialogDescription>Use the business hours, or set hours just for this branch.</DialogDescription></DialogHeader>
          <div className="flex gap-2" role="radiogroup">
            {[["default", "Use business hours"], ["custom", "Custom hours"]].map(([v, l]) => (
              <button key={v} role="radio" aria-checked={mode === v} onClick={() => setMode(v)} data-testid={`branch-hours-mode-${v}`} className={`rounded-full px-4 py-1.5 text-sm transition-colors ${mode === v ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-600"}`}>{l}</button>
            ))}
          </div>
          {mode === "custom" ? <WeeklyHoursEditor week={week} onChange={setWeek} prefix={`bh-${b.id}`} /> : <ul className="text-sm text-slate-600">{DAYS.map(([k, l]) => <li key={k} className="flex justify-between py-0.5"><span>{l}</span><span>{fmtPeriods(defaultWeek[k])}</span></li>)}</ul>}
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-slate-900 text-white" disabled={save.isPending || (mode === "custom" && !valid(week))} onClick={flow.go} data-testid={`branch-hours-save-${b.id}`}>Save</Button></DialogFooter>
          <ConflictDialog list={flow.list} onClose={flow.close} onConfirm={flow.confirm} busy={save.isPending} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function HoursTab({ onDirty }) {
  const { data, isLoading } = useQuery({ queryKey: ["business-hours"], queryFn: async () => (await api.get("/business/hours")).data });
  if (isLoading || !data) return <p className="text-sm text-slate-400">Loading…</p>;
  return (
    <div className="space-y-8">
      <section>
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="font-display text-base font-semibold text-slate-900">Business hours (default)</h3>
          <span className="text-xs text-slate-500" data-testid="hours-timezone">Times in {data.timezone}</span>
        </div>
        {!data.configured && <p className="mb-2 text-xs text-amber-700" data-testid="hours-not-configured">Not set yet — bookings are not checked against hours until you save them.</p>}
        <BusinessWeekly data={{ ...data, weekly: data.weekly || emptyWeek() }} onDirty={onDirty} />
      </section>
      {data.branches.length > 0 && (
        <section data-testid="branch-hours">
          <h3 className="mb-2 font-display text-base font-semibold text-slate-900">Branches</h3>
          <div className="grid gap-2 sm:grid-cols-2">{data.branches.map((b) => <BranchHours key={b.id} b={b} defaultWeek={data.weekly} canEdit={data.can_edit} />)}</div>
        </section>
      )}
      <SpecialDates branches={data.branches} canEdit={data.can_edit} />
    </div>
  );
}
